/**
 * @file NarrativeWidget.tsx
 *
 * @description A RJSF widget that provides an interface for editing narrative text
 * with AI-assisted features like condensing, expanding, name correction,
 * and regeneration with additional context.
 *
 * @module Widgets
 */
import { WidgetProps } from "@rjsf/utils";
import { useState } from "react";
import { Box } from "@mui/material";
import { NarrativeWidgetOptions, LoadingOperation } from "./narrative/types";
import { useNarrativeRegeneration } from "./narrative/useNarrativeRegeneration";
import { NarrativeEditor } from "./narrative/NarrativeEditor";
import { AIAssistanceTools } from "./narrative/AIAssistanceTools";
import {
  NarrativeConfirmationModal,
  OperationType,
} from "./narrative/NarrativeConfirmationModal";

export const NarrativeWidget = (props: WidgetProps) => {
  const {
    id,
    value,
    label,
    onChange,
    required,
    disabled,
    readonly,
    placeholder,
    schema,
    options,
    registry,
  } = props;

  // Extract custom options from both widget options and formContext
  const widgetOptions = options as NarrativeWidgetOptions;
  const formContext = registry?.formContext as
    | NarrativeWidgetOptions
    | undefined;

  /**
   * Converts RJSF widget id to a JSON path for json_query.
   * Example: "root_Narratives_0_Text" -> "root->Narratives->0->Text"
   */
  const convertIdToJsonPath = (widgetId: string): string => {
    return widgetId.replace("root_", "root→Narrative→").replace(/_/g, "→");
  };

  /**
   * Extracts the narrative index from the RJSF widget id.
   */
  const extractNarrativeIndex = (widgetId: string): number => {
    const match = widgetId.match(/Narratives?_(\d+)/i);
    return match ? parseInt(match[1], 10) : 0;
  };

  // Convert id to JSON path for agent API
  const narrativePath = convertIdToJsonPath(id);

  const narrativeIndex =
    widgetOptions?.narrativeIndex ??
    formContext?.narrativeIndex ??
    extractNarrativeIndex(id);
  const liveWebSocket =
    widgetOptions?.liveWebSocket ?? formContext?.liveWebSocket;
  const handleUserActivity =
    widgetOptions?.handleUserActivity ?? formContext?.handleUserActivity;
  const handleSaveChanges =
    widgetOptions?.handleSaveChanges ?? formContext?.handleSaveChanges;
  const showError = widgetOptions?.showError ?? formContext?.showError;
  const userProfile = widgetOptions?.userProfile ?? formContext?.userProfile;
  const chatSendMessage = formContext?.chatSendMessage;
  const setShowChat = formContext?.setShowChat;
  const showAIControls = formContext?.showAIControls;

  // State
  const [loadingOperation, setLoadingOperation] =
    useState<LoadingOperation>(null);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [confirmationAction, setConfirmationAction] = useState<
    (() => void) | null
  >(null);
  const [pendingOperationType, setPendingOperationType] =
    useState<OperationType>(null);

  // Hook for AI regeneration via chat
  const { handleNarrativeRegeneration } = useNarrativeRegeneration({
    liveWebSocket,
    narrativeIndex,
    narrativePath,
    showError,
    setLoadingOperation,
    chatSendMessage,
    setShowChat,
  });

  const minRows = (options?.rows as number) || 4;

  // Handlers

  const handleToFirstPerson = () => {
    setPendingOperationType("toFirstPerson");
    setConfirmationAction(() => async () => {
      setLoadingOperation("toFirstPerson");
      if (handleSaveChanges) await handleSaveChanges();
      await handleNarrativeRegeneration("TO_FIRST_PERSON", null);
      handleUserActivity?.(`Converted narrative to first-person perspective`);
    });
    setConfirmationModalOpen(true);
  };

  const handleToThirdPerson = (officerName: string) => {
    setPendingOperationType("toThirdPerson");
    setConfirmationAction(() => async () => {
      setLoadingOperation("toThirdPerson");
      if (handleSaveChanges) await handleSaveChanges();
      await handleNarrativeRegeneration("TO_THIRD_PERSON", { officerName });
      handleUserActivity?.(
        `Converted narrative to third-person perspective (${officerName})`,
      );
    });
    setConfirmationModalOpen(true);
  };

  const handleCorrectName = (nameToCorrect: string, correctName: string) => {
    if (!value) return;
    const updatedText = (value as string).replace(
      new RegExp(nameToCorrect.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g"),
      correctName,
    );
    onChange(updatedText);
    handleUserActivity?.(`Corrected name in narrative`);
  };

  const handleRegenerateWithContext = (context: string) => {
    setPendingOperationType("addContext");
    setConfirmationAction(() => async () => {
      setLoadingOperation("addContext");
      if (handleSaveChanges) await handleSaveChanges();
      await handleNarrativeRegeneration("ADD_CONTEXT", {
        additionalContext: context,
      });
      handleUserActivity?.(`Regenerated narrative with additional context`);
    });
    setConfirmationModalOpen(true);
  };

  const executeConfirmation = () => {
    if (confirmationAction) {
      confirmationAction();
    }
    setConfirmationModalOpen(false);
    setPendingOperationType(null);
  };

  return (
    <Box display="flex" flexDirection="column" gap={2} width="100%">
      <NarrativeEditor
        id={id}
        value={value}
        onChange={onChange}
        disabled={disabled || readonly || false}
        readonly={readonly || false}
        loadingOperation={loadingOperation}
        placeholder={placeholder}
        label={label}
        required={required || false}
        minRows={minRows}
        maxRows={12}
        description={schema.description}
      />
      {showAIControls && (
        <AIAssistanceTools
          loadingOperation={loadingOperation}
          disabled={disabled || readonly || false}
          onToFirstPerson={handleToFirstPerson}
          onToThirdPerson={handleToThirdPerson}
          onCorrectName={handleCorrectName}
          onRegenerateWithContext={handleRegenerateWithContext}
          userProfile={userProfile}
        />
      )}

      <NarrativeConfirmationModal
        open={confirmationModalOpen}
        onClose={() => {
          setConfirmationModalOpen(false);
          setPendingOperationType(null);
        }}
        onConfirm={executeConfirmation}
        operationType={pendingOperationType}
      />
    </Box>
  );
};
